import type { TFunction } from "i18next";
import { useState, useRef, useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import { FaSearch } from "react-icons/fa";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import U2000AutoCompleteInput, { U2000AutoCompleteInputResultTable } from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000InputTextBase from "../../../../u2000Core/components/formControls/input/U2000InputTextBase";
import U2000InputMask from "../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000KeyboardKeyHint from "../../../../u2000Core/components/common/U2000KeyboardKeyHint";
import U2000AutoCompletePanelResultsBlock, {
  U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS,
} from "../../../../u2000Core/components/common/U2000AutoCompletePanelResultsBlock";
import {
  createPanelCriteriaFocusHandler,
  U2000_AUTOCOMPLETE_PANEL_COMPACT,
} from "../../../../u2000Core/tools/u2000AutocompletePanelUtils";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumEditMode } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U3121NewCustomerDlg from "./U3121NewCustomerDlg";
import { NS } from "../../../U3121";
import type { U3121Project, U3121Customer } from "../../../U3121Service";
import { useU3121FindProjectManagerHandlers, useU3121FindCustomerHandlers } from "../../../U3121UseFindHandlers";
import { useU3121Context } from "../../../U3121Context";

interface U3121ProjectBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3121Project>;
  t: TFunction;
}

function U3121ProjectBody({ formStateHandlers, t }: U3121ProjectBodyProps) {
  const { initLU } = useU3121Context();
  const editMode = formStateHandlers.getFormEditMode();
  const [isNewCustomerDlgOpen, setIsNewCustomerDlgOpen] = useState(false);
  /** Visibilité du checkbox « Inclure sous-projet » : pilotée par la personnalisation adm_include_sub_projet. */
  const showIncludeSubProject = initLU.personalization?.admIncludeSubProject === "true";
  const findProjectManagerHandlers = useU3121FindProjectManagerHandlers();
  const findCustomerHandlers = useU3121FindCustomerHandlers();

  const onSearchProjectManager = findProjectManagerHandlers.search;

  const displayProjectManager = (item: Record<string, unknown>) => {
    const firstName = item.firstName as string;
    const lastName = item.lastName as string;
    const codeEmployee = item.codeEmployee as string;
    return `${firstName ?? ""} ${lastName ?? ""}`.trim() ? `${firstName} ${lastName} (${codeEmployee ?? ""})` : (codeEmployee ?? "");
  };

  const extractDataForProjectManager = (result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "projectManager.id", value: result.id },
        { fieldName: "projectManager.firstName", value: result.firstName },
        { fieldName: "projectManager.lastName", value: result.lastName },
        { fieldName: "projectManager.codeEmployee", value: result.codeEmployee },
      ];
    }
    return [{ fieldName: "projectManager", value: null }];
  };

  const onSearchCustomer = findCustomerHandlers.search;

  const displayCustomer = (item: Record<string, unknown>) => {
    const code = item.customerCode as string;
    const name = item.customerName as string;
    return name && code ? `${name} (${code})` : (name ?? code ?? "");
  };

  const extractDataForCustomer = (result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "customer.id", value: result.id },
        { fieldName: "customer.customerCode", value: result.customerCode },
        { fieldName: "customer.customerName", value: result.customerName },
        { fieldName: "customer.officePhone1", value: result.officePhone1 },
        { fieldName: "customer.officePhone2", value: result.officePhone2 },
      ];
    }
    return [
      { fieldName: "customer.id", value: null },
      { fieldName: "customer.customerCode", value: null },
      { fieldName: "customer.customerName", value: null },
      { fieldName: "customer.officePhone1", value: null },
      { fieldName: "customer.officePhone2", value: null },
    ];
  };

  type CustomerSearchPanelProps = {
    panelFields?: Record<string, unknown>;
    setPanelFields: Dispatch<SetStateAction<Record<string, unknown>>>;
    onPanelSearch: (fields: Record<string, unknown>) => void;
    results?: Record<string, unknown>[];
    totalCount?: number;
    truncated?: boolean;
    setShowPanel: (show: boolean) => void;
    handleRowClickFn?: (item: Record<string, unknown>) => void;
  };

  const CustomerSearchPanel = ({ panelFields = {}, setPanelFields, onPanelSearch, results = [], totalCount, truncated, setShowPanel, handleRowClickFn }: CustomerSearchPanelProps) => {
    const [showResults, setShowResults] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const customerCodeRef = useRef<HTMLInputElement | null>(null);

    // Handlers minimal pour U2000InputMask (panneau non lié au formulaire principal)
    const panelFormHandlers = useMemo(
      () => ({
        getData: () => panelFields,
        setData: (data: Record<string, unknown>) => setPanelFields((prev) => ({ ...prev, ...data })),
        onChange: (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean) => setPanelFields((prev) => ({ ...prev, [e.target.id]: value })),
        getFormState: () => U2000FormStateEnumState.EDIT,
        getTouched: () => ({}),
        getErrors: () => ({}),
        isSubmitted: () => false,
        onBlur: undefined as ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined,
      }),
      [panelFields, setPanelFields],
    );

    // S'assurer d'avoir toujours un tableau (au cas où le parent passerait le DTO)
    const list = Array.isArray(results)
      ? results
      : results && typeof results === "object" && "results" in results && Array.isArray((results as { results: unknown[] }).results)
        ? (results as { results: unknown[] }).results
        : [];

    useEffect(() => {
      if (list.length > 0) {
        setShowResults(true);
        setHighlightedIndex(-1);
      } else {
        setShowResults(false);
        setHighlightedIndex(-1);
      }
    }, [list.length]);

    const handleCriteriaFocus = createPanelCriteriaFocusHandler(setHighlightedIndex);

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setShowPanel(false);
          setShowResults(false);
        }
        if (e.key === "F3") {
          e.preventDefault();
          onPanelSearch(panelFields as Record<string, unknown>);
        }
      };
      window.addEventListener("keydown", handleKeyDown as EventListener);
      return () => window.removeEventListener("keydown", handleKeyDown as EventListener);
    }, [panelFields, onPanelSearch, setShowPanel]);

    useEffect(() => {
      if (panelRef.current) {
        try {
          panelRef.current.focus();
        } catch {
          // ignore
        }
        requestAnimationFrame(() => {
          if (customerCodeRef.current) {
            try {
              customerCodeRef.current.focus();
            } catch {
              // ignore
            }
          }
        });
      }
    }, []);

    const getFocusableElements = () => {
      if (!panelRef.current) return [] as HTMLElement[];
      const selectors = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", '[tabindex]:not([tabindex="-1"])'];
      return Array.from(panelRef.current.querySelectorAll(selectors.join(","))) as HTMLElement[];
    };

    const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Tab") return;
      const focusable = getFocusableElements().filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panelRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    };

    const handleRowClick = (item: Record<string, unknown>, idx: number) => {
      setShowPanel(false);
      setHighlightedIndex(-1);
      void idx;
      handleRowClickFn?.(item);
    };

    return (
      <div
        ref={panelRef}
        onKeyDown={handleTrapKeyDown}
        className={`flex h-full flex-col ${U2000_AUTOCOMPLETE_PANEL_COMPACT.rootGap} rounded-lg border border-gray-200 bg-white p-4 shadow-lg animate-fade-in`}
        tabIndex={0}
      >
        <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaToolbar}>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaFields} onFocusCapture={handleCriteriaFocus}>
            <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
              <div className="min-w-0 w-1/3">
                <U2000InputTextBase
                  placeholder={t("lblCustomerCode", { ns: NS })}
                  fieldName="customerCode"
                  type="string"
                  value={(panelFields.customerCode as string) ?? ""}
                  setValue={(value) => setPanelFields((prev) => ({ ...prev, customerCode: value ?? "" }))}
                  inputRef={customerCodeRef}
                />
              </div>
              <div className="min-w-0 w-2/3">
                <U2000InputTextBase
                  placeholder={t("lblCustomerName", { ns: NS })}
                  fieldName="customerName"
                  type="string"
                  value={(panelFields.customerName as string) ?? ""}
                  setValue={(value) => setPanelFields((prev) => ({ ...prev, customerName: value ?? "" }))}
                />
              </div>
            </div>
            <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
              <div className="min-w-0 flex-1 [&>div]:w-full">
                <U2000InputMask
                  formStateHandlers={panelFormHandlers as U2000FormStateHandlers<Record<string, unknown>>}
                  fieldName="officePhone1"
                  mask="phone"
                  placeholder={t("placeholderOfficePhonePrincipal1", { ns: NS })}
                />
              </div>
              <div className="flex shrink-0 items-center">
                <U2000CheckBox
                  formStateHandlers={panelFormHandlers as U2000FormStateHandlers<Record<string, unknown>>}
                  fieldName="isRegularCustomer"
                  label={t("lblIsRegularCustomer", { ns: NS })}
                />
              </div>
            </div>
          </div>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.searchRow}>
            <U2000ActionBtn
              className="w-full"
              label={t("btnSearch", { ns: "common" })}
              icon={FaSearch}
              trailingHint={<U2000KeyboardKeyHint>F3</U2000KeyboardKeyHint>}
              handleClick={() => onPanelSearch(panelFields as Record<string, unknown>)}
            />
          </div>
        </div>
        <U2000AutoCompletePanelResultsBlock highlightedIndex={highlightedIndex}>
          <U2000AutoCompleteInputResultTable
            results={list as Record<string, unknown>[]}
            resultLabels={[t("lblCustomerCode", { ns: NS }), t("lblCustomerName", { ns: NS }), t("lblOfficePhonePrincipal1", { ns: NS })]}
            resultFields={["customerCode", "customerName", "officePhone1"]}
            handleRowClick={handleRowClick}
            showResults={showResults}
            setShowResults={setShowResults}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            showHeader={false}
            className={U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS}
            totalCount={totalCount}
            truncated={truncated}
          />
        </U2000AutoCompletePanelResultsBlock>
      </div>
    );
  };

  return (
    <U2000Panel title={t("lblGeneralInfo", { ns: NS })}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <U2000InputText formStateHandlers={formStateHandlers} fieldName="projetNumber" label={t("lblProjetNumber", { ns: NS })} required={editMode === U2000FormStateEnumEditMode.EDIT} readonly />
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <U2000InputText formStateHandlers={formStateHandlers} fieldName="projetName" label={t("lblProjetName", { ns: NS })} required />
          </div>
          <div className="shrink-0 self-center pt-6">
            <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="projectStatus" label={t("valActive", { ns: NS })} readonly />
          </div>
        </div>
        <div className="min-w-0">
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers}
            fieldName="projectManager"
            mainFieldName="codeEmployee"
            entityLink="U3101"
            label={t("lblProjectLead", { ns: NS })}
            onSearch={onSearchProjectManager}
            displayFn={displayProjectManager}
            overrideExtractDataFn={extractDataForProjectManager}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={["firstName", "lastName", "codeEmployee"]}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <U2000AutoCompleteInput
              formStateHandlers={formStateHandlers}
              fieldName="customer"
              mainFieldName="search"
              entityLink="U3120"
              label={t("lblClient", { ns: NS })}
              required
              onSearch={onSearchCustomer}
              displayFn={displayCustomer}
              overrideExtractDataFn={extractDataForCustomer}
              minCarLaunchSearch={1}
              resultLabels={[t("lblCustomerCode", { ns: NS }), t("lblCustomerName", { ns: NS }), t("lblOfficePhonePrincipal1", { ns: NS })]}
              resultFields={["customerCode", "customerName", "officePhone1"]}
              customSearchPanelHeight={500}
              renderPanel={(props) => <CustomerSearchPanel {...props} />}
              resultTableShowHeader={false}
            />
          </div>
          <button
            type="button"
            onClick={() => setIsNewCustomerDlgOpen(true)}
            disabled={!(editMode === U2000FormStateEnumEditMode.ADD || editMode === U2000FormStateEnumEditMode.EDIT) || Boolean((formStateHandlers.getData()?.customer?.id ?? "").toString().trim())}
            className="shrink-0 px-4 mt-4 py-2.5 text-sm font-medium text-white rounded-lg border border-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-400 bg-blue-600 hover:bg-blue-700"
          >
            {t("btnNewCustomer", { ns: NS })}
          </button>
        </div>
        <U3121NewCustomerDlg
          isOpen={isNewCustomerDlgOpen}
          onClose={() => setIsNewCustomerDlgOpen(false)}
          onSuccess={(customer: U3121Customer) => {
            const data = formStateHandlers.getData();
            if (data) formStateHandlers.setData({ ...data, customer });
            setIsNewCustomerDlgOpen(false);
          }}
          t={t}
        />
        {showIncludeSubProject && (
          <div className="flex items-center">
            <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="includeSubProjet" label={t("lblIncludeSubProject", { ns: NS })} readonly={editMode !== U2000FormStateEnumEditMode.ADD} />
          </div>
        )}
      </div>
    </U2000Panel>
  );
}

export default U3121ProjectBody;
